import moment from 'moment';
import type {
  TripsMonitoredUsageData,
  TripGroupRow,
  AlertTypeRow,
} from '../../types/tripsMonitoredUsage';
import type { TripStatusApiUsageData } from '../../types/tripStatusApiUsage';
import type { TimeframeSelection } from '../../types/timeframe';
import { generatePeriods, Period } from './utils/generatePeriods';

// ─── Deterministic value helpers ─────────────────────────────────────────────
// Values are derived from a string seed so they stay stable across re-renders
// for the same date range, but differ when the selected range changes.

function hashString(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Deterministic integer in [min, max] derived from a seed string. */
function seededValue(seed: string, min: number, max: number): number {
  const ratio = hashString(seed) / 0xffffffff;
  return Math.round(min + ratio * (max - min));
}

/** Per-period scaling so daily/weekly/quarterly magnitudes look realistic. */
function periodScale(
  granularity: TimeframeSelection['granularity'],
  periodCount: number
): number {
  switch (granularity) {
    case 'daily':
      return 1 / 30;
    case 'weekly':
      return 1 / 4.3;
    case 'quarterly':
      return 3;
    case 'entireRange':
      return Math.max(periodCount, 1) * 12;
    case 'monthly':
    default:
      return 1;
  }
}



const TRIP_GROUP_SEEDS: Array<{ name: string; base: number }> = [
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:AutomationGroup', base: 110 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:CorporateTravel', base: 180 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:CrewOperations', base: 90 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:CharterServices', base: 45 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:ExecutiveTravel', base: 210 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:VIPTravelers', base: 65 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:SalesTeam', base: 155 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:GroundOperations', base: 78 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:LogisticsDivision', base: 132 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:EventCoordination', base: 40 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:GovernmentContracts', base: 95 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:MedicalTransport', base: 58 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:InternationalDesk', base: 168 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:RegionalOffices', base: 120 },
  { name: 'ACCOUNT_ID:10376 | CUSTOMER_ID:TrainingDivision', base: 52 },
];

const ALERT_TYPE_SEEDS: Array<{ name: string; base: number }> = [
  { name: 'Flight Arrival Delay', base: 200 },
  { name: 'Leg Baggage Change', base: 33 },
  { name: 'Connection Info', base: 5 },
  { name: 'Flight Equipment Change', base: 58 },
  { name: 'Flight Departure Delay', base: 130 },
  { name: 'Flight Diversion', base: 9 },
  { name: 'Flight Departed Late', base: 70 },
  { name: 'Flight Arrival Update', base: 120 },
  { name: 'Leg Departure Delay', base: 87 },
  { name: 'Trip Reminder', base: 275 },
  { name: 'Leg Departure Gate Change', base: 12 },
  { name: 'Leg Departure Info', base: 750 },
  { name: 'Flight Departure Gate Change', base: 160 },
  { name: 'Leg Departed', base: 500 },
  { name: 'Leg Arrival Gate Change', base: 142 },
  { name: 'Flight Departed', base: 490 },
  { name: 'Waiver Update', base: 56 },
  { name: 'Flight Arrival Gate Change', base: 2 },
  { name: 'Leg Not Monitorable', base: 260 },
  { name: 'Scheduled Departure Update', base: 525 },
  { name: 'Flight Arrived', base: 485 },
  { name: 'Leg Arrival Delay', base: 135 },
  { name: 'Flight Cancellation', base: 11 },
  { name: 'Leg Arrival Info', base: 650 },
  { name: 'Scheduled Arrival Update', base: 595 },
  { name: 'Flight Departure Update', base: 145 },
  { name: 'Waiver Match', base: 14 },
  { name: 'Leg Arrived', base: 550 },
];

const QUERY_BASE = 175;

// ─── Builders ────────────────────────────────────────────────────────────────

/**
 * Builds Trips Monitored dashboard data for the selected timeframe.
 * The chart categories, table period columns, table values and the
 * scorecard totals are all derived from the periods of the date range, so
 * changing the calendar selection re-renders every dependent component.
 */
export function buildTripsMonitoredData(
  timeframe: TimeframeSelection
): TripsMonitoredUsageData {
  const periods = generatePeriods(timeframe);
  const scale = periodScale(timeframe.granularity, periods.length);
  const categories = periods.map(p => p.axisLabel);

  // Trip group rows keyed by period label (insertion order = chronological).
  const tripGroupsTable: TripGroupRow[] = TRIP_GROUP_SEEDS.map(group => {
    const row: TripGroupRow = { tripGroup: group.name };
    periods.forEach(period => {
      const base = group.base * scale;
      row[period.label] = seededValue(
        `${group.name}|${period.key}`,
        Math.round(base * 0.7),
        Math.round(base * 1.3)
      );
    });
    return row;
  });

  // Alert type rows split by delivery channel (email / api / sms). Each channel
  // is generated deterministically and scaled so email > api > sms, and the
  // "all" view is the per-period sum across every channel.
  const buildChannelRows = (channel: string, factor: number): AlertTypeRow[] =>
    ALERT_TYPE_SEEDS.map(alert => {
      const row: AlertTypeRow = { alertType: alert.name };
      periods.forEach(period => {
        const base = alert.base * scale * factor;
        row[period.label] = seededValue(
          `${alert.name}|${channel}|${period.key}`,
          Math.round(base * 0.7),
          Math.round(base * 1.3)
        );
      });
      return row;
    });

  const emailAlertRows = buildChannelRows('email', 0.6);
  const apiAlertRows = buildChannelRows('api', 0.3);
  const smsAlertRows = buildChannelRows('sms', 0.1);

  const allAlertRows: AlertTypeRow[] = ALERT_TYPE_SEEDS.map((alert, i) => {
    const row: AlertTypeRow = { alertType: alert.name };
    periods.forEach(period => {
      row[period.label] =
        (emailAlertRows[i][period.label] as number) +
        (apiAlertRows[i][period.label] as number) +
        (smsAlertRows[i][period.label] as number);
    });
    return row;
  });

  const alertsByChannel = {
    all: allAlertRows,
    email: emailAlertRows,
    api: apiAlertRows,
    sms: smsAlertRows,
  };

  // Chart series: total trips monitored per period (sum across trip groups).
  const seriesData = periods.map(period =>
    tripGroupsTable.reduce(
      (sum, row) => sum + (row[period.label] as number),
      0
    )
  );

  const totalTripsMonitored = seriesData.reduce((a, b) => a + b, 0);
  const totalAlertsSent = allAlertRows.reduce(
    (sum, row) => sum + periods.reduce((s, p) => s + (row[p.label] as number), 0),
    0
  );
  // The upstream API returns totalEmailsSent as a distinct field that runs
  // larger than totalAlertsSent (e.g. 142,584 vs 24,359 ≈ 5.85x). Derive a
  // realistic value from the alert total rather than a single alert row.
  const totalEmailsSent = Math.round(totalAlertsSent * 5.85);

  return {
    metrics: {
      totalTripsMonitored,
      totalAlertsSent,
      totalEmailsSent,
    },
    chartData: {
      categories,
      periods: periods.map(p => p.date),
      rangeStart: timeframe.startDate,
      rangeEnd: timeframe.endDate,
      series: [{ name: 'Trips Monitored', data: seriesData }],
    },
    tripGroupsTable,
    alertsByChannel,
  };
}

/**
 * Builds Trip Status API dashboard data for the selected timeframe.
 * Per-period query volumes, totals, average and the max/min (with their
 * dates) are all derived from the periods of the date range.
 */
export function buildTripStatusApiData(
  timeframe: TimeframeSelection
): TripStatusApiUsageData {
  const periods = generatePeriods(timeframe);
  const scale = periodScale(timeframe.granularity, periods.length);
  const base = QUERY_BASE * scale;

  const perPeriod = periods.map(period => ({
    period,
    queries: seededValue(
      `queries|${period.key}`,
      Math.round(base * 0.6),
      Math.round(base * 1.4)
    ),
  }));

  const totalQueries = perPeriod.reduce((s, p) => s + p.queries, 0);
  const averageQueries = perPeriod.length
    ? Math.round(totalQueries / perPeriod.length)
    : 0;

  const maxEntry = perPeriod.reduce(
    (max, cur) => (cur.queries > max.queries ? cur : max),
    perPeriod[0]
  );
  const minEntry = perPeriod.reduce(
    (min, cur) => (cur.queries < min.queries ? cur : min),
    perPeriod[0]
  );

  const formatDate = (p?: Period): string =>
    p ? `On ${moment(p.date).format('MMM D, YYYY')}` : '';

  return {
    metrics: {
      totalQueries,
      averageQueries,
      maxQueries: maxEntry?.queries ?? 0,
      maxQueriesDate: formatDate(maxEntry?.period),
      minQueries: minEntry?.queries ?? 0,
      minQueriesDate: formatDate(minEntry?.period),
    },
    chartData: {
      categories: periods.map(p => p.axisLabel),
      periods: periods.map(p => p.date),
      rangeStart: timeframe.startDate,
      rangeEnd: timeframe.endDate,
      series: [{ name: 'Queries', data: perPeriod.map(p => p.queries) }],
    },
    queriesTable: perPeriod.map(p => ({
      month: p.period.label,
      queries: p.queries,
    })),
  };
}

// ─── Backwards-compatible static snapshots ───────────────────────────────────
// Some references expect ready-made objects. These are generated from a
// default 12-month monthly range so they retain the original shape.

const DEFAULT_RANGE: TimeframeSelection = {
  granularity: 'monthly',
  preset: 'past12Months',
  startDate: moment().subtract(11, 'months').startOf('month').format('YYYY-MM-DD'),
  endDate: moment().endOf('month').format('YYYY-MM-DD'),
};

export const mockTripsMonitoredData: TripsMonitoredUsageData =
  buildTripsMonitoredData(DEFAULT_RANGE);

export const mockTripStatusApiData: TripStatusApiUsageData =
  buildTripStatusApiData(DEFAULT_RANGE);
